import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import type { Citation } from "@/apis/cs-rag/types";
import { cn } from "@/lib/utils";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Zoom } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/zoom";

export interface CitationCardRef {
  zoomIn: () => void;
  zoomOut: () => void;
  resetTransform: () => void;
}

interface CitationCardProps {
  citation: Citation;
  domId: string;
  isHighlighted: boolean;
}

const ZOOM_STEP = 1.4;
const MIN_SCALE = 1;
const MAX_SCALE = 3;

export const CitationCard = forwardRef<CitationCardRef, CitationCardProps>(
  ({ citation, domId, isHighlighted }, ref) => {
    const swiperRef = useRef<SwiperType | null>(null);
    const scaleRef = useRef(MIN_SCALE);
    const images = citation.images ?? [];
    const hasImages = images.length > 0;
    const hasMultipleImages = images.length > 1;
    const [currentIndex, setCurrentIndex] = useState(0);

    useImperativeHandle(ref, () => ({
      zoomIn: () => {
        const swiper = swiperRef.current;
        if (!swiper?.zoom) return;
        const next = Math.min(scaleRef.current * ZOOM_STEP, MAX_SCALE);
        scaleRef.current = next;
        swiper.zoom.in(next);
      },
      zoomOut: () => {
        const swiper = swiperRef.current;
        if (!swiper?.zoom) return;
        const next = Math.max(scaleRef.current / ZOOM_STEP, MIN_SCALE);
        scaleRef.current = next;
        if (next <= MIN_SCALE) {
          swiper.zoom.out();
        } else {
          swiper.zoom.in(next);
        }
      },
      resetTransform: () => {
        const swiper = swiperRef.current;
        if (!swiper?.zoom) return;
        scaleRef.current = MIN_SCALE;
        swiper.zoom.out();
      },
    }));

    return (
      <div
        id={domId}
        className={cn(
          "transition-shadow scroll-mt-3",
          isHighlighted
            ? "rounded-[10px] shadow-[0_0_0_3px_rgba(37,99,235,0.35)]"
            : "",
        )}
      >
        {hasImages ? (
          <div
            className="overflow-hidden rounded-[10px] border border-line-alternative bg-white h-[calc(100vh-240px)]"
            style={
              { "--swiper-navigation-size": "24px" } as React.CSSProperties
            }
          >
            <Swiper
              key={citation.n}
              modules={[Navigation, Zoom]}
              navigation={hasMultipleImages}
              zoom={{ maxRatio: MAX_SCALE, minRatio: MIN_SCALE }}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
                scaleRef.current = MIN_SCALE;
              }}
              onSlideChange={(swiper) => {
                setCurrentIndex(swiper.activeIndex);
                scaleRef.current = MIN_SCALE;
              }}
              allowTouchMove={hasMultipleImages}
              className="h-full w-full"
            >
              {images.map((image, i) => (
                <SwiperSlide key={i}>
                  <div className="swiper-zoom-container h-full">
                    <img
                      src={image.data}
                      alt={`${citation.title} - p.${citation.page}${images.length > 1 ? ` (${i + 1}/${images.length})` : ""}`}
                      className="block w-full h-full object-contain"
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            {hasMultipleImages && (
              <div className="border-t border-line-alternative bg-white px-2 py-1.5 text-center">
                <span className="caption-1-semibold text-label-alternative">
                  {currentIndex + 1} / {images.length}
                </span>
              </div>
            )}
          </div>
        ) : (
          <p className="text-center caption-1-regular text-label-assistive py-3 px-3">
            이 출처에는 페이지 이미지가 없습니다
          </p>
        )}
      </div>
    );
  },
);

CitationCard.displayName = "CitationCard";
